import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import InputBase from '@mui/material/InputBase'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Add from '@mui/icons-material/Add'
import SearchIcon from '@mui/icons-material/Search'
import API from '../../component/Axios'
import EditIcon from '../../component/EditIcon'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import {
    LIST_FILTER_DIVIDER_SX,
    LIST_FILTER_SEARCH_BUTTON_SX,
    LIST_FILTER_SEARCH_FIELD_SX,
    LIST_FILTER_SELECT_FIELD_SX,
    ListFilterBar,
    ListFilterForm,
} from '../../component/ListFilterLayout'
import ListTableToolbar, { LIST_TABLE_TOOLBAR_BUTTON_SX } from '../../component/ListTableToolbar'
import ListTableContainer from '../../component/ListTableContainer'
import Pagination from '../../component/Pagination'
import ResetFilterButton from '../../component/ResetFilterButton'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import StatusChip from '../../component/StatusChip'
import Title from '../../component/Title'
import { buildListSearch as buildListSearchQuery, createInitialListState, createListLinkTarget, parseNumberParam } from '../../utils/listPageState'

const DEFAULT_SEARCH_SELECT = {
    searchType: '',
    searchStatus: 99,
}

const SEARCH_SELECT_PARAMS = {
    searchType: {
        param: 'type',
        defaultValue: DEFAULT_SEARCH_SELECT.searchType,
    },
    searchStatus: {
        param: 'status',
        defaultValue: DEFAULT_SEARCH_SELECT.searchStatus,
        parse: (value, defaultValue) => parseNumberParam(value, defaultValue),
    },
}

const TERRITORY_SKIN_STATUS_CHIP = {
    0: { label: '사용', color: 'success' },
    1: { label: '미사용', color: 'default', variant: 'outlined' },
}

const TYPE_LABELS = {
    color: '컬러',
    image: '이미지',
}

const APP_TERRITORY_SKIN_TABLE_COLUMNS = [
    { width: 70 },
    { width: 100 },
    { width: 128 },
    { width: 190 },
    { width: 170 },
    { width: 110 },
    { width: 100 },
    { width: 80 },
    { width: 120 },
    { width: 150 },
]

const APP_TERRITORY_SKIN_TABLE_MIN_WIDTH = APP_TERRITORY_SKIN_TABLE_COLUMNS.reduce((sum, column) => sum + column.width, 0)

const PREVIEW_IMAGE_SX = {
    width: 88,
    height: 56,
    objectFit: 'cover',
    border: '1px solid rgba(148, 163, 184, 0.35)',
    borderRadius: '4px',
    display: 'block',
}

const COLOR_SWATCH_SX = {
    width: 34,
    height: 34,
    border: '1px solid rgba(148, 163, 184, 0.45)',
    borderRadius: '4px',
    flexShrink: 0,
}

const NOWRAP_CELL_SX = {
    whiteSpace: 'nowrap',
}

const TEXT_CELL_SX = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
}

const getInitialListState = (location) => createInitialListState({
    location,
    defaultSearchSelect: DEFAULT_SEARCH_SELECT,
    selectParams: SEARCH_SELECT_PARAMS,
})

const buildListSearch = ({ curPage, searchQuery, searchSelect }) => buildListSearchQuery({
    curPage,
    searchQuery,
    searchSelect,
    selectParams: SEARCH_SELECT_PARAMS,
})

const getSkinRowKey = (row) => `${row.type}-${row.territorySkinId}`

const renderTypeLabel = (type) => TYPE_LABELS[type] || type || '-'

const renderPricePoint = (row) => {
    if (row.type !== 'image') {
        return '-'
    }

    const pricePoint = Number(row.pricePoint || 0)
    return pricePoint > 0 ? pricePoint.toLocaleString() : '무료'
}

const renderPreview = (row) => {
    if (row.type === 'image' && row.previewImageUrl) {
        return (
            <img
                src={row.previewImageUrl}
                alt={row.skinKey || ''}
                style={PREVIEW_IMAGE_SX}
            />
        )
    }

    if (row.color) {
        return (
            <Box
                aria-label={row.color}
                sx={{ ...COLOR_SWATCH_SX, backgroundColor: row.color }}
            />
        )
    }

    return '-'
}

const renderColorCell = (row) => {
    if (!row.color) {
        return '-'
    }

    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, maxWidth: '100%' }}>
            <Box sx={{ ...COLOR_SWATCH_SX, width: 22, height: 22, backgroundColor: row.color }} />
            <Box component="span" sx={TEXT_CELL_SX} title={row.color}>{row.color}</Box>
        </Box>
    )
}

export default function AppTerritorySkins({ adminId, role }) {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(getInitialListState(location))
    const initialListState = initialListStateRef.current
    const silentListRefreshRef = useRef(false)
    const statusInFlightRef = useRef(false)
    const [loading, setLoading] = useState(true)
    const [actionLoading, setActionLoading] = useState(false)
    const [totalCount, setTotalCount] = useState(0)
    const [params, setParams] = useState([])
    const [curPage, setCurPage] = useState(initialListState.curPage)
    const offset = 10
    const [openSnackbar, setOpenSnackbar] = useState(initialListState.snackbar.openSnackbar)
    const [severitySnackbar, setSeveritySnackbar] = useState(initialListState.snackbar.severitySnackbar)
    const [messageSnackbar, setMessageSnackbar] = useState(initialListState.snackbar.messageSnackbar)
    const [searchText, setSearchText] = useState(initialListState.searchText)
    const [searchQuery, setSearchQuery] = useState(initialListState.searchQuery)
    const [searchSelect, setSearchSelect] = useState(initialListState.searchSelect)
    const [listErrorMessage, setListErrorMessage] = useState('')

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/territorySkin/getList', {
                page: curPage,
                offset,
                adminId,
                role,
                searchText: searchQuery,
                searchSelect,
            })

            if (data.code === 200) {
                setTotalCount(data.data.count || 0)
                setParams(data.data.rows || [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || '땅따먹기 스킨 목록을 불러오지 못했습니다.'
                setTotalCount(0)
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = '땅따먹기 스킨 목록을 불러오지 못했습니다.'
            setTotalCount(0)
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [adminId, curPage, history, role, searchQuery, searchSelect])

    useEffect(() => {
        const showSpinner = !silentListRefreshRef.current
        silentListRefreshRef.current = false
        getData(showSpinner)
    }, [getData])

    useEffect(() => {
        const nextSearch = buildListSearch({ curPage, searchQuery, searchSelect })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [curPage, history, location.pathname, location.search, searchQuery, searchSelect])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const updateSearchSelect = (e) => {
        if (loading || actionLoading) {
            return
        }

        if (curPage === 1) {
            silentListRefreshRef.current = true
        }

        const { name, value } = e.target
        setSearchSelect(prev => ({
            ...prev,
            [name]: value,
        }))
        setCurPage(1)
    }

    const resetFilters = () => {
        if (loading || actionLoading) {
            return
        }

        setCurPage(1)
        setSearchText('')
        setSearchQuery('')
        setSearchSelect({
            ...DEFAULT_SEARCH_SELECT,
        })
    }

    const searchButtonClick = () => {
        if (loading || actionLoading) {
            return
        }

        const trimmedSearchText = (searchText || '').trim()

        if (curPage === 1 && searchQuery === trimmedSearchText) {
            getData(false)
            return
        }

        setSearchQuery(trimmedSearchText)

        if (curPage !== 1) {
            setCurPage(1)
        }
    }

    const handleSearchSubmit = (e) => {
        e.preventDefault()
        searchButtonClick()
    }

    const handleStatusChange = async (row) => {
        if (statusInFlightRef.current) {
            return
        }

        const nextStatus = Number(row.status) === 0 ? 1 : 0

        if (nextStatus === 1 && !window.confirm('미사용으로 변경하시겠습니까?')) {
            return
        }

        statusInFlightRef.current = true
        setActionLoading(true)

        try {
            const { data } = await API.post('/api/v1/admin/territorySkin/updateStatus', {
                territorySkinId: row.territorySkinId,
                type: row.type,
                status: nextStatus,
                adminId,
                role,
            })

            if (data.code === 200) {
                if (
                    curPage > 1
                    && params.length === 1
                    && String(searchSelect.searchStatus) === String(row.status)
                ) {
                    setCurPage(curPage - 1)
                } else {
                    await getData(false)
                }
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(nextStatus === 0 ? '사용 상태로 변경했습니다.' : '미사용 상태로 변경했습니다.')
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message || '상태를 변경하지 못했습니다.')
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('상태를 변경하지 못했습니다.')
        } finally {
            statusInFlightRef.current = false
            setActionLoading(false)
        }
    }

    const hasActiveFilters = Boolean((searchQuery || '').trim())
        || String(searchSelect.searchType) !== String(DEFAULT_SEARCH_SELECT.searchType)
        || String(searchSelect.searchStatus) !== String(DEFAULT_SEARCH_SELECT.searchStatus)

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="땅따먹기 스킨 목록을 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={loading || actionLoading}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        if (hasActiveFilters) {
            return (
                <ListEmptyState
                    title="조건에 맞는 땅따먹기 스킨이 없습니다."
                    description="검색어 또는 필터를 초기화한 뒤 다시 확인해보세요."
                    action={<ResetFilterButton label="필터 초기화" onClick={resetFilters} disabled={loading || actionLoading} />}
                />
            )
        }

        return (
            <ListEmptyState
                title="등록된 땅따먹기 스킨이 없습니다."
                description="컬러 또는 이미지 스킨이 등록되면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Title>APP 땅따먹기 스킨 리스트</Title>
                    <ListFilterBar>
                        <ListFilterForm onSubmit={handleSearchSubmit} trailing={<ResetFilterButton onClick={resetFilters} disabled={loading || actionLoading} />}>
                            <InputBase
                                sx={LIST_FILTER_SEARCH_FIELD_SX}
                                placeholder="스킨키/컬러"
                                type="search"
                                name="searchText"
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                disabled={loading || actionLoading}
                            />
                            <IconButton type="submit" sx={LIST_FILTER_SEARCH_BUTTON_SX} aria-label="search" disabled={loading || actionLoading}><SearchIcon /></IconButton>
                            <Divider sx={LIST_FILTER_DIVIDER_SX} orientation="vertical" />
                            <TextField
                                label="타입"
                                select
                                name="searchType"
                                value={searchSelect.searchType}
                                onChange={updateSearchSelect}
                                disabled={loading || actionLoading}
                                size="small"
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value="color">컬러</MenuItem>
                                <MenuItem value="image">이미지</MenuItem>
                            </TextField>
                            <TextField
                                label="상태"
                                select
                                name="searchStatus"
                                value={searchSelect.searchStatus}
                                onChange={updateSearchSelect}
                                disabled={loading || actionLoading}
                                size="small"
                                sx={LIST_FILTER_SELECT_FIELD_SX}
                            >
                                <MenuItem value={99}>전체</MenuItem>
                                <MenuItem value={0}>사용</MenuItem>
                                <MenuItem value={1}>미사용</MenuItem>
                            </TextField>
                        </ListFilterForm>
                    </ListFilterBar>

                    <ListTableToolbar
                        right={(
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<Add />}
                                size="small"
                                disabled={loading || actionLoading}
                                sx={LIST_TABLE_TOOLBAR_BUTTON_SX}
                                onClick={() => history.push(createListLinkTarget({
                                    pathname: '/new-appTerritorySkin',
                                    search: location.search,
                                    curPage,
                                    searchText: searchQuery,
                                    searchSelect,
                                }))}
                            >
                                등록
                            </Button>
                        )}
                    />
                    <ListTableContainer>
                    <Table size="small" sx={{ minWidth: APP_TERRITORY_SKIN_TABLE_MIN_WIDTH, tableLayout: 'fixed' }}>
                        <colgroup>
                            {APP_TERRITORY_SKIN_TABLE_COLUMNS.map((column, index) => (
                                <col key={`app-territory-skin-column-${index}`} style={{ width: column.width }} />
                            ))}
                        </colgroup>
                        <caption>Total Count: {totalCount}</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>No</TableCell>
                                <TableCell>타입</TableCell>
                                <TableCell>미리보기</TableCell>
                                <TableCell>스킨키</TableCell>
                                <TableCell>대표 컬러</TableCell>
                                <TableCell>판매 포인트</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>정렬</TableCell>
                                <TableCell>선택 회원</TableCell>
                                <TableCell align="right">Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {params.length > 0 ? (
                                params.map((row, index) => (
                                    <TableRow key={getSkinRowKey(row)}>
                                        <TableCell>{row.rownum || ((curPage - 1) * offset) + index + 1}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{renderTypeLabel(row.type)}</TableCell>
                                        <TableCell>{renderPreview(row)}</TableCell>
                                        <TableCell sx={TEXT_CELL_SX} title={row.skinKey || ''}>{row.skinKey || '-'}</TableCell>
                                        <TableCell sx={TEXT_CELL_SX}>{renderColorCell(row)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{renderPricePoint(row)}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>
                                            <StatusChip status={Number(row.status)} config={TERRITORY_SKIN_STATUS_CHIP} />
                                        </TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{row.sort ?? 0}</TableCell>
                                        <TableCell sx={NOWRAP_CELL_SX}>{row.selectedCount || 0}</TableCell>
                                        <TableCell align="right" sx={NOWRAP_CELL_SX}>
                                            <ListActionGroup disabled={actionLoading}>
                                                <EditIcon
                                                    path="appTerritorySkin"
                                                    id={`${row.type}/${row.territorySkinId}`}
                                                    type={row.type}
                                                    search={location.search}
                                                    curPage={curPage}
                                                    searchText={searchQuery}
                                                    searchSelect={searchSelect}
                                                    disabled={actionLoading}
                                                />
                                                <Button
                                                    type="button"
                                                    size="small"
                                                    variant="outlined"
                                                    color={Number(row.status) === 0 ? 'warning' : 'success'}
                                                    disabled={actionLoading}
                                                    onClick={() => handleStatusChange(row)}
                                                >
                                                    {Number(row.status) === 0 ? '미사용' : '사용'}
                                                </Button>
                                            </ListActionGroup>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={10}>
                                        {renderEmptyState()}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                    </ListTableContainer>
                    {totalCount > 0 && <Pagination curPage={curPage} offset={offset} totalCount={totalCount} setCurPage={setCurPage} />}
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
